export type JsonPrimitive = string | number | boolean | null;

export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

export type JsonObject = { [key: string]: JsonValue };

export const VEGA_LITE_JSON_TREE_LIMITS = {
  maxDepth: 64,
  maxNodes: 20_000,
} as const;

export type VegaLiteJsonTreeIssue = {
  message: string;
  path: string;
};

type JsonTreeValidation =
  | { status: 'valid'; value: JsonValue }
  | { issue: VegaLiteJsonTreeIssue; status: 'invalid' };

type TreeEntry =
  | { depth: number; kind: 'value'; path: string; value: unknown }
  | { kind: 'leave'; value: object };

export function isJsonPrimitive(value: unknown): value is JsonPrimitive {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    (typeof value === 'number' && Number.isFinite(value))
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function childPath(path: string, key: string): string {
  const segment = key.replace(/~/g, '~0').replace(/\//g, '~1');

  return path === '/' ? `/${segment}` : `${path}/${segment}`;
}

/**
 * Runs before cloning, formatting or any recursive consumer. Count scheduled values, not just
 * visited containers, so a wide tree or a shared subtree cannot bypass the work budget. Ancestors
 * are removed on exit: shared (but acyclic) objects are valid JSON and count once per occurrence.
 */
export function validateVegaLiteJsonTree(value: unknown): JsonTreeValidation {
  const pending: TreeEntry[] = [{ depth: 0, kind: 'value', path: '/', value }];
  const ancestors = new WeakSet<object>();
  let nodes = 1;

  while (pending.length > 0) {
    const entry = pending.pop();

    if (entry === undefined) {
      break;
    }

    if (entry.kind === 'leave') {
      ancestors.delete(entry.value);
      continue;
    }

    const { depth, path, value: current } = entry;

    if (depth > VEGA_LITE_JSON_TREE_LIMITS.maxDepth) {
      return {
        issue: {
          message: `JSON nesting depth must not exceed ${VEGA_LITE_JSON_TREE_LIMITS.maxDepth}. Simplify the nested value.`,
          path,
        },
        status: 'invalid',
      };
    }

    if (isJsonPrimitive(current)) {
      continue;
    }

    if (!Array.isArray(current) && !isRecord(current)) {
      return {
        issue: {
          message: 'Use only JSON values. Remove functions, undefined and non-finite numbers.',
          path,
        },
        status: 'invalid',
      };
    }

    if (ancestors.has(current)) {
      return {
        issue: { message: 'JSON values must not contain circular references.', path },
        status: 'invalid',
      };
    }

    const keys = Array.isArray(current) ? undefined : Object.keys(current);
    const childCount = Array.isArray(current) ? current.length : keys?.length ?? 0;
    nodes += childCount;

    if (nodes > VEGA_LITE_JSON_TREE_LIMITS.maxNodes) {
      return {
        issue: {
          message: `JSON must not exceed ${VEGA_LITE_JSON_TREE_LIMITS.maxNodes} values. Simplify the document.`,
          path,
        },
        status: 'invalid',
      };
    }

    ancestors.add(current);
    pending.push({ kind: 'leave', value: current });

    if (Array.isArray(current)) {
      for (let index = current.length - 1; index >= 0; index -= 1) {
        pending.push({
          depth: depth + 1,
          kind: 'value',
          path: childPath(path, String(index)),
          value: current[index],
        });
      }
    } else {
      for (let index = (keys?.length ?? 0) - 1; index >= 0; index -= 1) {
        const key = keys?.[index];

        if (key !== undefined) {
          pending.push({
            depth: depth + 1,
            kind: 'value',
            path: childPath(path, key),
            value: current[key],
          });
        }
      }
    }
  }

  return { status: 'valid', value: value as JsonValue };
}

/** Typed internal helpers must also refuse unsafe values when called without a decoder. */
export function assertVegaLiteJsonTree(value: unknown): void {
  const validation = validateVegaLiteJsonTree(value);

  if (validation.status === 'invalid') {
    throw new Error(`${validation.issue.path}: ${validation.issue.message}`);
  }
}
